import { faPencil } from "@fortawesome/pro-regular-svg-icons";
import { Box, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useCallback } from "react";
import { useBoolean } from "usehooks-ts";

import { Icon } from "../Icon/Icon";
import { IconButton } from "../IconButton/IconButton";
import { CoverFilesContent } from "./CoverFilesContent";
import { EditDialog } from "./EditDialog";
import { CoverFile } from "./types";

interface CoverFilesProps {
  files: CoverFile[];
  canEdit: boolean;
  isBusy?: boolean;
  onFilesChanged?: (newFileIds: string[]) => void;
}

const classes = {
  editButton: "edit-button",
};

const Container = styled(Stack)({
  position: "relative",
  height: "100%",
  width: "100%",
  [`&:hover .${classes.editButton}`]: {
    opacity: 1,
  },
});

const EditButtonContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1,
  opacity: 0,
  transition: "opacity 0.2s ease-in-out",
  [theme.breakpoints.down("md")]: {
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}));

export const CoverFiles = (props: CoverFilesProps) => {
  const { files, canEdit, onFilesChanged, isBusy } = props;

  const {
    value: isEditorDialogOpen,
    setTrue: openEditorDialog,
    setFalse: closeEditorDialog,
  } = useBoolean();

  const handleFilesChanged = useCallback(
    (newFileIds: string[]) => {
      onFilesChanged?.(newFileIds);
    },
    [onFilesChanged],
  );

  return (
    <Container>
      <CoverFilesContent files={files} />

      {canEdit && (
        <>
          <EditButtonContainer className={classes.editButton}>
            <IconButton
              aria-label="Edit cover images"
              variant="white"
              size="medium"
              onClick={openEditorDialog}
            >
              <Icon icon={faPencil} size="large" />
            </IconButton>
          </EditButtonContainer>
          <EditDialog
            open={isEditorDialogOpen}
            onClose={closeEditorDialog}
            files={files}
            isBusy={isBusy}
            onFilesChanged={handleFilesChanged}
          />
        </>
      )}
    </Container>
  );
};
